// src/pages/users/EditUserPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Users, 
  Upload, 
  X, 
  AlertCircle, 
  MapPin, 
  Phone, 
  Mail,
  User,
  Calendar,
  Hash,
  Building2,
  Warehouse,
  Eye,
  EyeOff,
  Check
} from 'lucide-react';
import { updateUser, getUser, getUserRoles, checkEmailAvailability } from '../../services/api/users';
import { getShops } from '../../services/api/shops';
import { getWarehouses } from '../../services/api/warehouses';
import { getImageUrl } from '../../utils/imageUtils';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './CreateUserPage.module.css';

const EditUserPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    birthday: '',
    employeeNumber: '',
    roleId: '',
    password: '',
    confirmPassword: '',
    isActive: true,
    shopIds: [],
    defaultShopId: '',
    warehouseIds: [],
    defaultWarehouseId: '',
    profilePicture: null
  });
  
  const [originalData, setOriginalData] = useState({});
  const [roles, setRoles] = useState([]);
  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profilePreview, setProfilePreview] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  // Fetch user data and other required data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        
        // Fetch user data and other resources in parallel
        const [userResponse, rolesResponse, shopsResponse, warehousesResponse] = await Promise.all([
          getUser(userId),
          getUserRoles(),
          getShops(),
          getWarehouses()
        ]);
        
        const user = userResponse;
        
        // Set up form data from user response
        const userData = {
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          address: user.address || '',
          birthday: user.birthday ? user.birthday.split('T')[0] : '',
          employeeNumber: user.employeeNumber || '',
          roleId: user.UserRole?.id || '',
          password: '',
          confirmPassword: '',
          isActive: user.isActive,
          shopIds: user.shops?.map(shop => shop.id) || [],
          defaultShopId: user.defaultShop?.id || '',
          warehouseIds: user.warehouses?.map(warehouse => warehouse.id) || [],
          defaultWarehouseId: user.defaultWarehouse?.id || '',
          profilePicture: null
        };
        
        setFormData(userData);
        setOriginalData(userData);
        
        // Set profile picture preview if exists
        if (user.profilePicture) {
          setProfilePreview(getImageUrl(user.profilePicture));
        }
        
        setRoles(rolesResponse);
        setShops(shopsResponse);
        setWarehouses(warehousesResponse);
        
      } catch (error) {
        console.error('Failed to fetch data:', error);
        if (error.response?.status === 404) {
          setErrors({ fetch: 'User not found. It may have been deleted.' });
        } else {
          setErrors({ fetch: 'Failed to load user data. Please try again.' });
        }
      } finally {
        setIsLoadingData(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId]);

  // Check for changes
  useEffect(() => {
    const hasFormChanges = Object.keys(formData).some(key => {
      if (key === 'password' || key === 'confirmPassword') {
        return changePassword && formData[key] !== '';
      }
      if (key === 'profilePicture') {
        return formData[key] !== null;
      }
      if (Array.isArray(formData[key])) {
        return JSON.stringify(formData[key]) !== JSON.stringify(originalData[key]);
      }
      return formData[key] !== originalData[key];
    });
    
    setHasChanges(hasFormChanges || changePassword);
  }, [formData, originalData, changePassword]);

  // Debounced email validation (only if email changed)
  useEffect(() => {
    if (!formData.email || formData.email === originalData.email) {
      setEmailStatus(null);
      return;
    }

    if (!formData.email.includes('@') || formData.email.length < 5) {
      setEmailStatus(null);
      return;
    }

    // Basic email format check before API call
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setEmailStatus(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsCheckingEmail(true);
        const response = await checkEmailAvailability(formData.email, userId);
        setEmailStatus(response.isTaken ? 'taken' : 'available');
      } catch (error) {
        console.error('Failed to check email:', error);
        setEmailStatus(null);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [formData.email, originalData.email, userId]);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = t('users.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('users.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('users.invalidEmail');
    } else if (emailStatus === 'taken') {
      newErrors.email = t('users.emailTaken');
    }

    if (!formData.roleId) {
      newErrors.roleId = t('users.roleRequired');
    }

    // Password validation (only if changing password)
    if (changePassword) {
      if (!formData.password) {
        newErrors.password = t('users.passwordRequired');
      } else if (formData.password.length < 6) {
        newErrors.password = t('users.passwordMinLength');
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t('users.confirmPasswordRequired');
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('users.passwordsDoNotMatch');
      }
    }

    // Phone validation (if provided)
    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = t('users.invalidPhone');
    }

    // Shop validation
    if (formData.shopIds.length > 0 && !formData.defaultShopId) {
      newErrors.defaultShopId = t('users.defaultShopRequired');
    }

    // Warehouse validation
    if (formData.warehouseIds.length > 0 && !formData.defaultWarehouseId) {
      newErrors.defaultWarehouseId = t('users.defaultWarehouseRequired');
    }

    // Profile picture validation (if provided)
    if (formData.profilePicture) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(formData.profilePicture.type)) {
        newErrors.profilePicture = t('users.invalidProfilePictureFormat');
      } else if (formData.profilePicture.size > 5 * 1024 * 1024) { // 5MB
        newErrors.profilePicture = t('users.profilePictureTooLarge');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear email status when email changes
    if (name === 'email') {
      setEmailStatus(null);
    }
  };

  const handleShopSelection = (shopId, isSelected) => {
    setFormData(prev => {
      const newShopIds = isSelected 
        ? [...prev.shopIds, parseInt(shopId)]
        : prev.shopIds.filter(id => id !== parseInt(shopId));
      
      // Clear default shop if it's no longer in selected shops
      const newDefaultShopId = newShopIds.includes(parseInt(prev.defaultShopId)) 
        ? prev.defaultShopId 
        : newShopIds.length > 0 ? newShopIds[0].toString() : '';

      return {
        ...prev,
        shopIds: newShopIds,
        defaultShopId: newDefaultShopId
      };
    });

    if (errors.defaultShopId) {
      setErrors(prev => ({ ...prev, defaultShopId: '' }));
    }
  };

  const handleWarehouseSelection = (warehouseId, isSelected) => {
    setFormData(prev => {
      const newWarehouseIds = isSelected 
        ? [...prev.warehouseIds, parseInt(warehouseId)]
        : prev.warehouseIds.filter(id => id !== parseInt(warehouseId));
      
      // Clear default warehouse if it's no longer in selected warehouses
      const newDefaultWarehouseId = newWarehouseIds.includes(parseInt(prev.defaultWarehouseId)) 
        ? prev.defaultWarehouseId 
        : newWarehouseIds.length > 0 ? newWarehouseIds[0].toString() : '';

      return {
        ...prev,
        warehouseIds: newWarehouseIds,
        defaultWarehouseId: newDefaultWarehouseId
      };
    });

    if (errors.defaultWarehouseId) {
      setErrors(prev => ({ ...prev, defaultWarehouseId: '' }));
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profilePicture: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setProfilePreview(e.target.result);
      reader.readAsDataURL(file);
    }

    if (errors.profilePicture) {
      setErrors(prev => ({
        ...prev,
        profilePicture: ''
      }));
    }
  };

  const handleRemoveProfilePicture = () => {
    setFormData(prev => ({
      ...prev,
      profilePicture: null
    }));
    setProfilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBack = async () => {
    if (hasChanges) {
      const shouldDiscard = await confirm(
        t('users.confirmDiscardChanges'),
        {
          confirmText: t('users.discardChanges'),
          cancelText: t('common.cancel')
        }
      );
      
      if (!shouldDiscard) return;
    }
    
    navigate('/users');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!hasChanges) {
      error(t('users.noChangesToSave'));
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Prepare form data for multipart/form-data
      const submitData = new FormData();
      
      // Only include fields that have changed
      if (formData.name !== originalData.name) {
        submitData.append('name', formData.name.trim());
      }
      
      if (formData.email !== originalData.email) {
        submitData.append('email', formData.email.trim());
      }
      
      if (formData.roleId !== originalData.roleId) {
        submitData.append('roleId', parseInt(formData.roleId));
      }
      
      if (formData.isActive !== originalData.isActive) {
        submitData.append('isActive', formData.isActive);
      }
      
      // Optional fields that might have changed
      if (formData.phone !== originalData.phone) {
        submitData.append('phone', formData.phone.trim());
      }
      
      if (formData.address !== originalData.address) {
        submitData.append('address', formData.address.trim());
      }
      
      if (formData.birthday !== originalData.birthday) {
        submitData.append('birthday', formData.birthday);
      }
      
      if (formData.employeeNumber !== originalData.employeeNumber) {
        submitData.append('employeeNumber', formData.employeeNumber.trim());
      }
      
      if (formData.profilePicture) {
        submitData.append('profilePicture', formData.profilePicture);
      }

      // Password (only if changing)
      if (changePassword && formData.password) {
        submitData.append('password', formData.password);
      }

      // Shop assignments
      if (JSON.stringify(formData.shopIds) !== JSON.stringify(originalData.shopIds)) {
        submitData.append('shopIds', JSON.stringify(formData.shopIds));
        if (formData.shopIds.length > 0) {
          submitData.append('defaultShopId', parseInt(formData.defaultShopId));
        }
      }

      // Warehouse assignments
      if (JSON.stringify(formData.warehouseIds) !== JSON.stringify(originalData.warehouseIds)) {
        submitData.append('warehouseIds', JSON.stringify(formData.warehouseIds));
        if (formData.warehouseIds.length > 0) {
          submitData.append('defaultWarehouseId', parseInt(formData.defaultWarehouseId));
        }
      }

      const response = await updateUser(userId, submitData);
      success(t('users.userUpdatedSuccess', { userName: response.user.name }), {
        title: t('common.success'),
        duration: 4000
      });
      navigate('/users');
    } catch (error) {
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else {
        setErrors({ submit: t('users.failedToUpdate') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className={styles.createUserPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Users className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('users.editUser')}</h1>
                <p>{t('users.updateTeamMember')}</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button 
                onClick={() => navigate('/users')} 
                className={styles.backButton}
              >
                <ArrowLeft size={20} />
                {t('users.backToUsers')}
              </button>
            </div>
          </div>
        </div>

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('users.loadingUser')}</h3>
            <p>{t('users.loadingUserDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (errors.fetch) {
    return (
      <div className={styles.createUserPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Users className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('users.editUser')}</h1>
                <p>{t('users.updateTeamMember')}</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button 
                onClick={() => navigate('/users')} 
                className={styles.backButton}
              >
                <ArrowLeft size={20} />
                {t('users.backToUsers')}
              </button>
            </div>
          </div>
        </div>

        <div className={styles.pageContent}>
          <div className={styles.errorState}>
            <AlertCircle className={styles.errorIcon} size={48} />
            <h3>{t('errors.loadingError')}</h3>
            <p>{errors.fetch}</p>
            <button onClick={() => window.location.reload()} className={styles.retryButton}>
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createUserPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Users className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('users.editUser')}</h1>
              <p>{t('users.updateTeamMember')}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button 
              onClick={handleBack} 
              className={styles.backButton}
            >
              <ArrowLeft size={20} />
              {t('users.backToUsers')}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        {errors.submit && (
          <div className={styles.errorBanner}>
            <AlertCircle size={20} />
            <div>
              <p>{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.userForm}>
          {/* Basic Information */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('users.basicInformation')}</h2>
              <p>{t('users.basicInformationDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">{t('users.fullName')} *</label>
                  <div className={styles.inputWithIcon}>
                    <User size={18} className={styles.inputIcon} />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('users.fullNamePlaceholder')}
                      className={errors.name ? 'error' : ''}
                      required
                    />
                  </div>
                  {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email">{t('users.emailAddress')} *</label>
                  <div className={styles.emailInputWrapper}>
                    <div className={styles.inputWithIcon}>
                      <Mail size={18} className={styles.inputIcon} />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('users.emailPlaceholder')}
                        className={`${errors.email ? 'error' : ''} ${emailStatus === 'available' ? 'success' : ''}`}
                        required
                      />
                      <div className={styles.emailStatus}>
                        {isCheckingEmail && <div className={styles.checkingSpinner}></div>}
                        {!isCheckingEmail && emailStatus === 'available' && (
                          <Check size={16} className={`${styles.statusIcon} ${styles.success}`} />
                        )}
                        {!isCheckingEmail && emailStatus === 'taken' && (
                          <X size={16} className={`${styles.statusIcon} ${styles.error}`} />
                        )}
                      </div>
                    </div>
                  </div>
                  {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                  {!errors.email && emailStatus === 'available' && (
                    <span className={styles.successText}>{t('users.emailAvailable')}</span>
                  )}
                  {!errors.email && emailStatus === 'taken' && (
                    <span className={styles.errorText}>{t('users.emailTaken')}</span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="phone">{t('users.phoneNumber')}</label>
                  <div className={styles.inputWithIcon}>
                    <Phone size={18} className={styles.inputIcon} />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('users.phonePlaceholder')}
                      className={errors.phone ? 'error' : ''}
                    />
                  </div>
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="employeeNumber">{t('users.employeeNumber')}</label>
                  <div className={styles.inputWithIcon}>
                    <Hash size={18} className={styles.inputIcon} />
                    <input
                      type="text"
                      id="employeeNumber"
                      name="employeeNumber"
                      value={formData.employeeNumber}
                      onChange={handleInputChange}
                      placeholder={t('users.employeeNumberPlaceholder')}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="address">{t('users.address')}</label>
                  <div className={styles.inputWithIcon}>
                    <MapPin size={18} className={styles.inputIcon} />
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder={t('users.addressPlaceholder')}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="birthday">{t('users.birthday')}</label>
                  <div className={styles.inputWithIcon}>
                    <Calendar size={18} className={styles.inputIcon} />
                    <input
                      type="date"
                      id="birthday"
                      name="birthday"
                      value={formData.birthday}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Picture */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('users.profilePicture')}</h2>
              <p>{t('users.profilePictureDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.profileUploadSection}>
                {!profilePreview ? (
                  <div className={styles.profileUploadArea} onClick={() => fileInputRef.current?.click()}>
                    <Upload size={32} className={styles.uploadIcon} />
                    <p className={styles.uploadText}>{t('users.clickToUpload')}</p>
                    <p className={styles.uploadHint}>{t('users.profilePictureFormats')}</p>
                  </div>
                ) : (
                  <div className={styles.profilePreview}>
                    <img src={profilePreview} alt="Profile preview" className={styles.previewImage} />
                    <button
                      type="button"
                      className={styles.removeProfileButton}
                      onClick={handleRemoveProfilePicture}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleProfilePictureChange}
                  style={{ display: 'none' }}
                />
                
                {errors.profilePicture && <span className={styles.errorText}>{errors.profilePicture}</span>}
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('users.accountSettings')}</h2>
              <p>{t('users.accountSettingsDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.formGroup}>
                <label htmlFor="roleId">{t('users.userRole')} *</label>
                <select
                  id="roleId"
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleInputChange}
                  className={errors.roleId ? 'error' : ''}
                  required
                >
                  <option value="">{t('users.selectRole')}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.roleId && <span className={styles.errorText}>{errors.roleId}</span>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={changePassword}
                    onChange={(e) => {
                      setChangePassword(e.target.checked);
                      if (!e.target.checked) {
                        setFormData(prev => ({
                          ...prev,
                          password: '',
                          confirmPassword: ''
                        }));
                        setErrors(prev => ({
                          ...prev,
                          password: '',
                          confirmPassword: ''
                        }));
                      }
                    }}
                  />
                  <span className={styles.checkmark}>
                    {changePassword && <Check size={14} />}
                  </span>
                  {t('users.changePassword')}
                </label>
              </div>

              {changePassword && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="password">{t('users.password')} *</label>
                    <div className={styles.passwordWrapper}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={t('users.passwordPlaceholder')}
                        className={errors.password ? 'error' : ''}
                        required={changePassword}
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.password && <span className={styles.errorText}>{errors.password}</span>}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="confirmPassword">{t('users.confirmPassword')} *</label>
                    <div className={styles.passwordWrapper}>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder={t('users.confirmPasswordPlaceholder')}
                        className={errors.confirmPassword ? 'error' : ''}
                        required={changePassword}
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
                  </div>
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <span className={styles.checkmark}>
                    {formData.isActive && <Check size={14} />}
                  </span>
                  {t('users.accountIsActive')}
                </label>
              </div>
            </div>
          </div>

          {/* Shop Assignment */}
          {shops.length > 0 && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('users.shopAssignment')}</h2>
                <p>{t('users.shopAssignmentDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.assignmentGrid}>
                  {shops.map((shop) => (
                    <label key={shop.id} className={styles.assignmentItem}>
                      <input
                        type="checkbox"
                        checked={formData.shopIds.includes(shop.id)}
                        onChange={(e) => handleShopSelection(shop.id, e.target.checked)}
                      />
                      <span className={styles.checkmark}>
                        {formData.shopIds.includes(shop.id) && <Check size={14} />}
                      </span>
                      <div className={styles.assignmentInfo}>
                        <Building2 size={16} className={styles.assignmentIcon} />
                        <span className={styles.assignmentName}>{shop.name}</span>
                        <span className={styles.assignmentType}>{shop.type}</span>
                      </div>
                    </label>
                  ))}
                </div>

                {formData.shopIds.length > 0 && (
                  <div className={styles.formGroup}>
                    <label htmlFor="defaultShopId">{t('users.defaultShop')} *</label>
                    <select
                      id="defaultShopId"
                      name="defaultShopId"
                      value={formData.defaultShopId}
                      onChange={handleInputChange}
                      className={errors.defaultShopId ? 'error' : ''}
                      required
                    >
                      <option value="">{t('users.selectDefaultShop')}</option>
                      {shops
                        .filter(shop => formData.shopIds.includes(shop.id))
                        .map((shop) => (
                          <option key={shop.id} value={shop.id}>
                            {shop.name}
                          </option>
                        ))}
                    </select>
                    {errors.defaultShopId && <span className={styles.errorText}>{errors.defaultShopId}</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Warehouse Assignment */}
          {warehouses.length > 0 && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('users.warehouseAssignment')}</h2>
                <p>{t('users.warehouseAssignmentDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.assignmentGrid}>
                  {warehouses.map((warehouse) => (
                    <label key={warehouse.id} className={styles.assignmentItem}>
                      <input
                        type="checkbox"
                        checked={formData.warehouseIds.includes(warehouse.id)}
                        onChange={(e) => handleWarehouseSelection(warehouse.id, e.target.checked)}
                      />
                      <span className={styles.checkmark}>
                        {formData.warehouseIds.includes(warehouse.id) && <Check size={14} />}
                      </span>
                      <div className={styles.assignmentInfo}>
                        <Warehouse size={16} className={styles.assignmentIcon} />
                        <span className={styles.assignmentName}>{warehouse.name}</span>
                        {warehouse.address && (
                          <span className={styles.assignmentAddress}>{warehouse.address}</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>

                {formData.warehouseIds.length > 0 && (
                  <div className={styles.formGroup}>
                    <label htmlFor="defaultWarehouseId">{t('users.defaultWarehouse')} *</label>
                    <select
                      id="defaultWarehouseId"
                      name="defaultWarehouseId"
                      value={formData.defaultWarehouseId}
                      onChange={handleInputChange}
                      className={errors.defaultWarehouseId ? 'error' : ''}
                      required
                    >
                      <option value="">{t('users.selectDefaultWarehouse')}</option>
                      {warehouses
                        .filter(warehouse => formData.warehouseIds.includes(warehouse.id))
                        .map((warehouse) => (
                          <option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </option>
                        ))}
                    </select>
                    {errors.defaultWarehouseId && <span className={styles.errorText}>{errors.defaultWarehouseId}</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.formActions}>
            <button 
              type="button" 
              onClick={handleBack}
              className={styles.cancelButton}
            >
              <X size={18} />
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className={`${styles.submitButton} ${!hasChanges ? styles.disabled : ''}`}
              disabled={isLoading || isCheckingEmail || emailStatus === 'taken' || !hasChanges}
            >
              {isLoading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                <>
                  <Save size={18} />
                  {hasChanges ? t('users.updateUser') : t('users.noChanges')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserPage;